import {
    faBuilding,
    faCalendarAlt,
    faFileInvoiceDollar,
    faHome,
    faLayerGroup,
    faMapMarkerAlt,
    faUserShield,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useRef, useState } from "react";
import {
    Card,
    Col,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import capmoLogo from "../../assets/images/capmo_logo.svg";
import { ModalAtom } from "../../components/atoms/atom";
import { FilterBar } from "../../components/molecule/molecule";
import { ACCESS_RIGHT_AREAS } from "../../constant/permissions";
import useInfiniteScroll from "../../hooks/useInfiniteScroll";
import {
    projectsApi,
    useCreateProjectMutation,
    useDeleteProjectMutation,
    useGetProjectsQuery,
    useUpdateProjectMutation,
} from "../../store/services/projectsService";
import { getSearchAddFilter } from "../../utils/get-filter-items";
import isGlobalAdminRole from "../../utils/isGlobalAdmin";
import { getFormattedDate, showToast } from "../../utils/method";
import AddProjectModal from "./add/add-project";
import AssignProjectAdminModal from "./assign-project-admin-modal";
import "./assign-project-admin-modal.scss";
import "./projects.scss";

function SkeletonProjectCard() {
    return (
        <Col xs={12} sm={6} md={4} lg={3} className="mb-4">
            <div className="project-card-skeleton">
                <div className="sk-accent" />
                <div className="sk-body">
                    <div className="sk-line sk-line--title" />
                    <div className="sk-line sk-line--md" />
                    <div className="sk-line sk-line--sm" />
                </div>
                <div className="sk-footer">
                    {[0, 1, 2].map((i) => (
                        <div key={i} className="sk-stat">
                            <div className="sk-stat__label" />
                            <div className="sk-stat__value" />
                        </div>
                    ))}
                </div>
            </div>
        </Col>
    );
}

function ProjectsPage() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [page, setPage] = useState(1);
    const [filter, setFilter] = useState({ search: "", status: "default" });

    const {
        data: projectsResponse,
        isLoading: loading,
        isFetching: loadingMore,
    } = useGetProjectsQuery({
        page,
        limit: 50,
        search: filter.search,
        status: filter.status,
    });

    const projectsList = projectsResponse?.items ?? [];
    const pagination = {
        page,
        limit: 50,
        total: projectsResponse?.total ?? 0,
        totalPages: projectsResponse?.after?.totalPages ?? 0,
    };
    const hasMore = page < (projectsResponse?.after?.totalPages ?? 0);

    const [createProject] = useCreateProjectMutation();
    const [updateProject] = useUpdateProjectMutation();
    const [deleteProject] = useDeleteProjectMutation();

    // Local state for modal data
    const [projectModalView, setProjectModalView] = useState("");
    const [selectedProject, setSelectedProject] = useState({});
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [assignAdminProject, setAssignAdminProject] = useState(null);
    const searchTimerRef = useRef(null);

    const { user: currentUser } = useSelector((state) => state.auth);
    const isGlobalAdmin = isGlobalAdminRole(currentUser?.role);

    // Create filter items based on the stored filter
    const [filterItems, setFilterItems] = useState(
        getSearchAddFilter(
            {
                addTextKey: "projects.add-project",
            },
            [
                {
                    key: "status",
                    type: "dropdown",
                    label: t("status"),
                    sortOptions: false,
                    value: filter.status,
                    shouldTranslate: true,
                    options: [
                        {
                            value: "all",
                            label: "filter-bar.status.all-projects",
                        },
                        {
                            value: "default",
                            label: "filter-bar.status.default-projects",
                        },
                        {
                            value: "local",
                            label: "filter-bar.status.local-projects",
                        },
                        {
                            value: "capmo",
                            label: "filter-bar.status.capmo-projects",
                        },
                        {
                            value: "archived",
                            label: "filter-bar.status.archived-projects",
                        },
                    ],
                },
            ],
        ),
    );

    // Sync filterItems UI values when filter state changes.
    useEffect(() => {
        // Update the filterItems' values based on Redux state
        const updatedItems = filterItems.map((item) => {
            if (item.key === "search" || item.key === "status") {
                return { ...item, value: filter[item.key] || "" };
            }
            return item;
        });
        setFilterItems(updatedItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const loadNextPage = useCallback(() => {
        setPage((p) => p + 1);
    }, []);

    const sentinelRef = useInfiniteScroll(loadNextPage, hasMore, loading || loadingMore);

    const onFilterAction = (index, key, info) => {
        if (key === "search") {
            const value = info?.value || "";
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = setTimeout(() => {
                setFilter((prev) => ({ ...prev, search: value }));
                setPage(1);
            }, 300);
        } else if (key === "status") {
            setFilter((prev) => ({ ...prev, status: info?.value || "" }));
            setPage(1);
        }

        if (key === "add") {
            setSelectedProject({});
            setProjectModalView("add");
        }
    };

    const onAddProject = async (projectData) => {
        try {
            if (projectModalView === "add") {
                await createProject(projectData).unwrap();
                showToast(t("projects.save-success"), { type: "success" });
            } else if (projectModalView === "edit") {
                const { id, name, owner, address, projectKey, projectVolume, startDate } = projectData;
                const updatePayload = { name, owner, address, projectKey, projectVolume, startDate };
                await updateProject({ id, ...updatePayload }).unwrap();
                showToast(t("projects.update-success"), { type: "success" });
            }

            setProjectModalView("");
            // RTK invalidatesTags refetches the LIST automatically.
        } catch (error) {
            showToast(error?.data?.message || error?.message || t("errors.general"), { type: "error" });
        }
    };

    const onDeleteProject = async () => {
        try {
            await deleteProject(selectedProject.id).unwrap();
            showToast(t("projects.delete-success"), { type: "success" });
            setShowDeleteModal(false);
        } catch (error) {
            showToast(error?.data?.message || error?.message || t("errors.general"), { type: "error" });
        }
    };

    const onCardClick = (projectId) => {
        navigate(`/projects/${projectId}`);
    };

    const stopCardNavigation = (event) => {
        event.stopPropagation();
    };

    const openAssignAdminModal = (event, project) => {
        stopCardNavigation(event);
        setAssignAdminProject(project);
    };

    const handleAdminAssigned = (adminInfo, projectId) => {
        if (!projectId || !adminInfo?.name) return;
        // Optimistic local update of the cached list row.
        dispatch(
            projectsApi.util.updateQueryData(
                "getProjects",
                { search: filter.search, status: filter.status },
                (draft) => {
                    const row = (draft?.items || []).find((p) => p.id === projectId);
                    if (row) {
                        row.projectAdmin = {
                            memberId: adminInfo.memberId,
                            userId: adminInfo.userId,
                            name: adminInfo.name,
                        };
                    }
                },
            ),
        );
    };

    // Custom tooltips for LV and invoice counts
    const renderLvTooltip = (project) => (
        <Tooltip id={`lv-tooltip-${project.id}`} className="count-tooltip">
            <div>
                <strong>{t("lv.mainLvCount")}:</strong>{" "}
                {project.lvCounts?.main || 0}
            </div>
            <div>
                <strong>{t("lv.nachtragLvCount")}:</strong>{" "}
                {project.lvCounts?.nachtrag || 0}
            </div>
        </Tooltip>
    );

    const renderInvoiceTooltip = (project) => (
        <Tooltip id={`invoice-tooltip-${project.id}`} className="count-tooltip">
            <div>
                <strong>{t("invoice.status.INVOICE_DRAFT")}:</strong>{" "}
                {project.invoiceCounts?.invoiceDraft || 0}
            </div>
            <div>
                <strong>{t("invoice.status.INVOICE_FINALIZED")}:</strong>{" "}
                {project.invoiceCounts?.invoiceFinalized || 0}
            </div>
            <div>
                <strong>{t("invoice.status.INVOICE_REJECTED")}:</strong>{" "}
                {project.invoiceCounts?.invoiceRejected || 0}
            </div>
        </Tooltip>
    );

    return (
        <div className="page-container">
            <FilterBar
                items={filterItems}
                onFilterAction={onFilterAction}
                area={ACCESS_RIGHT_AREAS.PROJECTS}
            />

            {pagination.total > 0 && (
                <div className="projects-results-bar text-end">
                    {t("projects.showing-count", { shown: projectsList.length, total: pagination.total })}
                </div>
            )}

            {/* Fixed height container with scrollable project cards area inside */}
            <div
                className="flex-grow-1 position-relative overflow-hidden"
            >
                {loading && projectsList.length === 0 ? (
                    <div className="project-cards-container overflow-auto h-100">
                        <Row className="project-cards">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <SkeletonProjectCard key={i} />
                            ))}
                        </Row>
                    </div>
                ) : projectsList.length === 0 ? (
                    <div className="no-projects-found">
                        {t("projects.no-data")}
                    </div>
                ) : (
                    /* This div is the scrollable container just for the project cards */
                    <div className="project-cards-container overflow-auto h-100">
                        {loading && (
                            <div className="projects-loading-bar">
                                <div className="projects-loading-bar__fill" />
                            </div>
                        )}
                        <Row className="project-cards">
                            {projectsList.map((project, index) => (
                                <Col
                                    key={project.id}
                                    xs={12}
                                    sm={6}
                                    md={4}
                                    lg={3}
                                    className="mb-4"
                                >
                                    <Card
                                        onClick={() => onCardClick(project.id)}
                                        className={`project-card h-100 ${project.isArchived ? "project-card--archived" : project.isFromCapmo ? "project-card--capmo" : project.isLocal ? "project-card--local" : "project-card--default"}`}
                                    >
                                        <div className="project-card-accent" />
                                        <Card.Body className="project-card-body">
                                            {/* Header row: name + indicators */}
                                            <div className="project-card-header">
                                                <div className="project-name-ellipsis">
                                                    {project.name}
                                                </div>
                                                <div className="project-indicators">
                                                    {project.isArchived && (
                                                        <span className="project-badge project-badge-archived" title={t("filter-bar.status.archived")}>
                                                            {t("filter-bar.status.archived")}
                                                        </span>
                                                    )}
                                                    {project.isLocal && (
                                                        <span className="project-badge project-badge-local" title={t("projects.local-project")}>
                                                            <FontAwesomeIcon icon={faHome} />
                                                        </span>
                                                    )}
                                                    {project.isFromCapmo && (
                                                        <span title={t("projects.from-capmo")}>
                                                            <img src={capmoLogo} alt="CAPMO" height="16" width="auto" />
                                                        </span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Project key */}
                                            {project.projectKey && (
                                                <div className="project-key">
                                                    <FontAwesomeIcon icon={faBuilding} />
                                                    <span>{project.projectKey}</span>
                                                </div>
                                            )}

                                            {/* Address */}
                                            <div className="project-address">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                                <span>{project.address || "-"}</span>
                                            </div>

                                            {isGlobalAdmin && (
                                                <div
                                                    className={`project-admin${project.projectAdmin?.name ? " project-admin--assigned" : " project-admin--empty"}`}
                                                    onClick={stopCardNavigation}
                                                >
                                                    <div className="project-admin__main">
                                                        <FontAwesomeIcon
                                                            icon={faUserShield}
                                                            className="project-admin__icon"
                                                        />
                                                        <span className="project-admin__label">
                                                            {t("projects.admin-short-label")}
                                                        </span>
                                                        <span
                                                            className="project-admin__divider"
                                                            aria-hidden="true"
                                                        >
                                                            ·
                                                        </span>
                                                        {project.projectAdmin?.name ? (
                                                            <span
                                                                className="project-admin__name"
                                                                title={project.projectAdmin.name}
                                                            >
                                                                {project.projectAdmin.name}
                                                            </span>
                                                        ) : (
                                                            <span className="project-admin__empty">
                                                                {t("projects.no-project-admin-short")}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {!project.projectAdmin?.name && (
                                                        <button
                                                            type="button"
                                                            className="project-admin__action"
                                                            onClick={(e) =>
                                                                openAssignAdminModal(
                                                                    e,
                                                                    project,
                                                                )
                                                            }
                                                        >
                                                            {t("projects.assign-admin.cta")}
                                                        </button>
                                                    )}
                                                </div>
                                            )}

                                        </Card.Body>

                                        {/* Stats footer */}
                                        <div className="project-card-stats">
                                            <div className="stat-cell">
                                                <div className="stat-cell-label">{t("projects.startDate")}</div>
                                                <div className="stat-cell-bottom">
                                                    <FontAwesomeIcon icon={faCalendarAlt} />
                                                    <span className="stat-cell-value">{getFormattedDate(project.startDate) || "-"}</span>
                                                </div>
                                            </div>
                                            <OverlayTrigger placement="top" delay={{ show: 250, hide: 400 }} overlay={renderLvTooltip(project)}>
                                                <div className="stat-cell stat-cell--clickable">
                                                    <div className="stat-cell-label">{t("projects.lv")}</div>
                                                    <div className="stat-cell-bottom">
                                                        <FontAwesomeIcon icon={faLayerGroup} />
                                                        <span className="stat-cell-value">{project.lvCounts?.total || 0}</span>
                                                    </div>
                                                </div>
                                            </OverlayTrigger>
                                            <OverlayTrigger placement="top" delay={{ show: 250, hide: 400 }} overlay={renderInvoiceTooltip(project)}>
                                                <div className="stat-cell stat-cell--clickable">
                                                    <div className="stat-cell-label">{t("invoice.invoice")}</div>
                                                    <div className="stat-cell-bottom">
                                                        <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                                        <span className="stat-cell-value">{project.invoiceCounts?.total || 0}</span>
                                                    </div>
                                                </div>
                                            </OverlayTrigger>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                            {loadingMore && Array.from({ length: 4 }).map((_, i) => (
                                <SkeletonProjectCard key={`loading-more-${i}`} />
                            ))}
                        </Row>
                        {/* Sentinel triggers next-page fetch when scrolled into view */}
                        <div ref={sentinelRef} className="py-1" />
                    </div>
                )}
            </div>

            {projectModalView && (
                <AddProjectModal
                    mode={projectModalView}
                    projectInfo={selectedProject}
                    show
                    onClose={() => {
                        setProjectModalView("");
                        setSelectedProject({});
                    }}
                    onSubmit={onAddProject}
                />
            )}
            <ModalAtom
                title={t("projects.delete-project")}
                body={<p>{t("projects.delete-project-warning")}</p>}
                saveText="delete"
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleSubmit={onDeleteProject}
            />

            <AssignProjectAdminModal
                show={Boolean(assignAdminProject)}
                project={assignAdminProject}
                onClose={() => setAssignAdminProject(null)}
                onAssigned={handleAdminAssigned}
            />
        </div>
    );
}

export default ProjectsPage;
